import { ui } from "@patient-watch/ui";
import { Columns3 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AdminColumnDef } from "./types";

interface AdminColumnsDialogProps {
    columns: AdminColumnDef[];
    /** Currently visible column keys. */
    visibleKeys: string[];
    onSave: (visibleKeys: string[]) => void;
}

/** Draft-then-Save column visibility dialog (RawData Columns pattern). */
export function AdminColumnsDialog({ columns, visibleKeys, onSave }: AdminColumnsDialogProps) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<Set<string>>(() => new Set(visibleKeys));

    useEffect(() => {
        if (open) setDraft(new Set(visibleKeys));
    }, [open, visibleKeys]);

    const defaultKeys = useMemo(() => new Set(columns.filter((c) => c.defaultVisible).map((c) => c.key)), [columns]);
    const isNonDefault = visibleKeys.length !== defaultKeys.size || visibleKeys.some((key) => !defaultKeys.has(key));

    const toggle = (key: string) => {
        setDraft((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <ui.DialogTrigger asChild>
                <ui.Button variant="outline" size="sm" type="button" className="relative shrink-0">
                    <Columns3 className="w-4 h-4" />
                    Columns
                    {isNonDefault ? (
                        <span className="top-1 right-1 absolute bg-sky-500 rounded-full w-1.5 h-1.5" aria-hidden />
                    ) : null}
                </ui.Button>
            </ui.DialogTrigger>
            <ui.DialogContent className="sm:max-w-sm">
                <ui.DialogHeader>
                    <ui.DialogTitle>Columns</ui.DialogTitle>
                    <ui.DialogDescription>Choose which columns to show in the table.</ui.DialogDescription>
                </ui.DialogHeader>
                <div className="space-y-2 max-h-72 overflow-auto">
                    {columns.map((column) => (
                        <label key={column.key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <ui.Checkbox
                                checked={draft.has(column.key)}
                                onCheckedChange={() => toggle(column.key)}
                                aria-label={`Show ${column.label}`}
                            />
                            {column.label}
                        </label>
                    ))}
                </div>
                <ui.DialogFooter className="gap-2">
                    <ui.Button type="button" variant="ghost" size="sm" onClick={() => setDraft(new Set(defaultKeys))}>
                        Reset
                    </ui.Button>
                    <ui.Button
                        type="button"
                        size="sm"
                        onClick={() => {
                            const ordered = columns.filter((c) => draft.has(c.key)).map((c) => c.key);
                            onSave(
                                ordered.length > 0 ? ordered : columns.filter((c) => c.defaultVisible).map((c) => c.key)
                            );
                            setOpen(false);
                        }}
                    >
                        Save
                    </ui.Button>
                </ui.DialogFooter>
            </ui.DialogContent>
        </ui.Dialog>
    );
}
